import React, { useState } from 'react';
import logoImg from '../../static/images/logo.png';
import authImg from '../../static/images/auth.jpeg';
import Modal from '../ui/Modal';

export default function AuthLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Panel - auth.jpeg image */}
      <div className="hidden md:block md:w-1/2 relative">
        <img
          src={authImg}
          alt="Education"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-indigo-800/70 to-purple-900/80" />
        <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-14">
          <div>
            <img src={logoImg} alt="Athenian" className="h-10 w-auto max-w-[180px] object-contain object-left brightness-0 invert" />
          </div>

          <div className="max-w-md">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Empowering SPED Professionals
            </h1>
            <p className="text-indigo-200 text-base leading-relaxed">
              Streamline your special education management with powerful tools designed for compliance, collaboration, and student success.
            </p>

            <div className="mt-8 space-y-3">
              <FeatureItem text="HIPAA & FERPA compliant environment" />
              <FeatureItem text="Real-time compliance tracking & analytics" />
              <FeatureItem text="Collaborative case management tools" />
            </div>
          </div>

          <div className="flex items-center gap-3 text-indigo-300/60 text-xs">
            <span>&copy; 2026 Athenian</span>
            <span>&bull;</span>
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 md:w-1/2 flex flex-col min-h-screen bg-white dark:bg-slate-900 overflow-y-auto">
        {/* Mobile header with logo */}
        <div className="md:hidden px-6 pt-6 pb-2">
          <img src={logoImg} alt="Athenian" className="h-9 w-auto max-w-[168px] object-contain object-left" />
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-5 sm:px-10 py-6 sm:py-8">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>

        {/* Bottom: Privacy & Contact */}
        <AuthFooter />
      </div>
    </div>
  );
}

function AuthFooter() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="px-5 sm:px-10 pb-6 flex items-center justify-center gap-3 sm:gap-6 flex-wrap text-xs text-gray-400">
      <button
        type="button"
        onClick={() => setPrivacyOpen(true)}
        className="hover:text-gray-600 dark:hover:text-slate-300 transition-colors flex items-center gap-1"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Privacy Policy
      </button>

      <span className="text-gray-300 dark:text-slate-600">&bull;</span>

      <button
        type="button"
        onClick={() => setContactOpen(true)}
        className="hover:text-gray-600 dark:hover:text-slate-300 transition-colors flex items-center gap-1"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        Contact Us
      </button>

      <span className="text-gray-300 dark:text-slate-600">&bull;</span>
      <span>&copy; 2026 Athenian</span>

      <Modal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" size="sm">
        <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
          We take your privacy seriously. All data is encrypted and stored securely. We comply with HIPAA and FERPA regulations to protect student and staff information.
        </p>
      </Modal>

      <Modal open={contactOpen} onClose={() => setContactOpen(false)} title="Contact Support" size="sm">
        <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
          Email: support@athenian.com<br />
          Phone: (800) 555-0199<br />
          Hours: Mon–Fri 8AM–6PM EST
        </p>
      </Modal>
    </div>
  );
}

function FeatureItem({ text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
        <svg className="w-3.5 h-3.5 text-indigo-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <span className="text-indigo-100 text-sm">{text}</span>
    </div>
  );
}
